import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { Autocomplete, Box, CircularProgress, FormControl, ListItem, Stack, TextField, Typography, createFilterOptions, } from '@mui/material';
import React from 'react';
import { icons } from '../icons/icons';
import { buildEventTab, buildGeneralTab, buildHiddenTab, buildStyleTab, buildValidateTab, showWidgetEditDialog } from '../WidgetEditDialog';
import { buildValuesTab } from '../WidgetEditDialog/buildValuesTab';
import { FormHelperErrorDescription, FormTooltip } from '../WidgetForm/Components';
import { defaultWidgetLabelProvider, getLabelAndValuePairs, getValueKey, getVisibleError, handleWidgetUpdate, isSkipValidate, joinLabels, loadValuesIfNeeded, makeNewConfigUnique, renderForm, validateCustom, validateUniqueInList, } from '../WidgetForm/Utils';
import { getFormLabelSuffix } from '../WidgetForm/Utils/getFormLabelSuffix';
import { type } from './types';
const WidgetFormSelect = (props) => {
    var _a, _b, _c, _d;
    const value = props.getData();
    const values = (_b = (_a = props.config.values) !== null && _a !== void 0 ? _a : props.internalData.values) !== null && _b !== void 0 ? _b : [];
    const option = (_c = values.find((option) => (props.config.valueEquals ? props.config.valueEquals(option.value, value) : option.value === value))) !== null && _c !== void 0 ? _c : null;
    const error = getVisibleError(props);
    const formLabelSuffix = getFormLabelSuffix(props);
    const elementLabel = formLabelSuffix ? props.config.label + formLabelSuffix : props.config.label;
    const disabled = props.config.disabled === true || (props.config.disabled && props.config.disabled.whenNoValues && values.length === 0);
    const readOnly = props.config.readOnly || ((_d = props.rootParams.formParams) === null || _d === void 0 ? void 0 : _d.readOnly);
    const filterOptions = props.config.showDescription
        ? createFilterOptions({
            stringify: (option) => option.label + (option.description ? ' ' + option.description : ''),
        })
        : undefined;
    const getIcon = (iconName) => {
        var _a;
        if (!iconName) {
            return;
        }
        const customIcons = (_a = props.rootParams.globalWidgetConfig) === null || _a === void 0 ? void 0 : _a.selectIcons;
        const Icon = customIcons ? customIcons[iconName] : icons[iconName];
        if (Icon) {
            return React.createElement(Icon, null);
        }
        return;
    };
    const renderOptions = props.config.showDescription
        ? (renderProps, option) => (React.createElement(Box, Object.assign({}, renderProps, { sx: {
                display: 'flex',
                flexDirection: 'column',
                m: 1,
                p: 1,
                backgroundColor: themeColors.surface.secondary,
            } }),
            React.createElement(Typography, { variant: 'subtitle2', sx: { alignSelf: 'flex-start' } }, option.label),
            option.description && (React.createElement(Typography, { variant: 'body2', color: themeColors.text.subtle, sx: { alignSelf: 'flex-start' } }, option.description))))
        : (renderProps, option) => (React.createElement(ListItem, Object.assign({}, renderProps),
            React.createElement(Stack, { direction: 'row', alignItems: 'center', gap: 1 },
                getIcon(option.icon),
                option.label)));
    const ariaDescribedBy = React.useId();
    return (React.createElement(Autocomplete, { id: props.id, fullWidth: true, size: 'medium', value: option, options: values, disabled: disabled, readOnly: readOnly, openOnFocus: true, loading: props.internalData.loading, filterOptions: filterOptions, groupBy: props.config.groupValues ? (option) => { var _a; return (_a = option.group) !== null && _a !== void 0 ? _a : ''; } : undefined, renderOption: renderOptions, getOptionKey: (option) => getValueKey(option.value), getOptionDisabled: (option) => { var _a; return (_a = option.disabled) !== null && _a !== void 0 ? _a : false; }, renderInput: (params) => {
            var _a, _b, _c;
            return (React.createElement(FormControl, { fullWidth: params.fullWidth, variant: 'outlined', size: params.size, required: (_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required, error: !!error, disabled: params.disabled, sx: {
                    '& label.MuiInputLabel-root': { paddingRight: 3 },
                } },
                React.createElement(FormTooltip, { tooltip: props.config.tooltip },
                    React.createElement(TextField, Object.assign({}, params, { tabIndex: props.editMode ? -1 : undefined, InputProps: Object.assign(Object.assign({}, params.InputProps), { startAdornment: getIcon(option === null || option === void 0 ? void 0 : option.icon), endAdornment: (React.createElement(React.Fragment, null,
                                props.internalData.loading ? React.createElement(CircularProgress, { color: 'inherit', size: 20 }) : null,
                                params.InputProps.endAdornment)), 'aria-describedby': ariaDescribedBy }), label: elementLabel, required: (_b = props.config.validate) === null || _b === void 0 ? void 0 : _b.required, error: !!error, autoFocus: (_c = (!props.editMode && props.config.autoFocus)) !== null && _c !== void 0 ? _c : false }))),
                React.createElement(FormHelperErrorDescription, { descriptionId: ariaDescribedBy, error: error, description: props.config.description })));
        }, onChange: (event, option) => {
            if (readOnly) {
                return;
            }
            const newValue = option === null || option === void 0 ? void 0 : option.value;
            props.setData(newValue);
            renderForm(props, { changed: true, autoSaveDebounce: props.config.autoSave });
        } }));
};
const WidgetFormSelectEditor = (props) => {
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, {
            title: 'Select: ' + props.config.label,
        }, buildGeneralTab(props, {
            key: true,
            label: true,
            appendRowIndexToLabel: true,
            description: true,
            tooltip: true,
            disabled: true,
            readOnly: true,
            autoFocus: true,
            onSubmit: (data) => {
                if (typeof data.disabled === 'object' && !data.disabled.whenNoValues) {
                    delete data.disabled;
                }
                return data;
            },
            children: [
                {
                    type: 'form.select',
                    key: 'defaultValue',
                    label: 'Default Value',
                    values: props.config.values,
                    valueProvider: props.config.valueProvider,
                },
                {
                    type: 'form.checkbox',
                    key: 'disabled.whenNoValues',
                    label: 'Disabled When No Values',
                },
                {
                    type: 'form.checkbox',
                    key: 'groupValues',
                    label: 'Enable Group Headers',
                },
            ],
        }), buildValuesTab(props, { description: 'if-show-description' }), buildValidateTab(props, { required: true, uniqueInList: true }), buildHiddenTab(props), buildStyleTab(props), buildEventTab(props, { eventOnChange: true, autoSave: true })) }),
        React.createElement(WidgetFormSelect, Object.assign({}, props))));
};
export const widgetFormSelect = {
    type,
    init: (props) => {
        if (props.getData() == null && props.config.defaultValue != null) {
            props.setData(props.config.defaultValue);
        }
        props.internalData.loadValues = true;
        loadValuesIfNeeded(props, false, false);
    },
    validate: (props) => {
        var _a, _b, _c, _d, _e;
        if (isSkipValidate(props)) {
            return;
        }
        const value = props.getData();
        if ((_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required) {
            if (value == null) {
                return (_b = props.config.validate.requiredMessage) !== null && _b !== void 0 ? _b : (props.config.label ? props.config.label + ' is required' : 'Required');
            }
        }
        if (((_c = props.config.validate) === null || _c === void 0 ? void 0 : _c.uniqueInList) || ((_d = props.config.validate) === null || _d === void 0 ? void 0 : _d.uniqueInGrid)) {
            const error = validateUniqueInList(props, value, props.dataPath);
            if (error) {
                return (_e = props.config.validate.uniqueInListMessage) !== null && _e !== void 0 ? _e : error;
            }
        }
        return validateCustom(props, value);
    },
    update: (props) => {
        loadValuesIfNeeded(props, false, false);
        return handleWidgetUpdate(props);
    },
    getLabelValueAsTextPairs: (props, labelPrefix) => {
        var _a, _b, _c;
        const data = props.getData();
        if (data) {
            const configValues = (_b = (_a = props.config.values) !== null && _a !== void 0 ? _a : props.internalData.values) !== null && _b !== void 0 ? _b : [];
            return getLabelAndValuePairs(joinLabels(labelPrefix, props.config.label), (_c = configValues.find((configValue) => configValue.value === data)) === null || _c === void 0 ? void 0 : _c.label);
        }
        return [];
    },
    component: WidgetFormSelect,
    icon: React.createElement(FormatListBulletedIcon, { fontSize: 'small' }),
    editorComponent: WidgetFormSelectEditor,
    createWidget: (rootConfig, editorPath, widgets) => makeNewConfigUnique(rootConfig, editorPath, widgets, { type, key: 'select', label: 'Select', values: [] }),
};
